import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { IconButton } from "@similarweb/ui-components";

import GrandAccessBanner from "app/pages/sales-intelligence/common-components/GrandAccessBanner/GrandAccessBanner";
import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { getIconForSignalType } from "app/pages/sales-intelligence/modules/signals/constants/signalTypeIcons";
import { requestSignalTypeAccessBySignalType } from "app/pages/sales-intelligence/modules/signals/services/requestSignalTypeAccess";
import { SuccessBannerContent } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/IntentDataBanner/SuccessBannerContent";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";
import { featureHookKeys } from "app/pages/sales-intelligence/utils/i18n/feature-hook";

type SignalTypeLockedBannerProps = {
    signalType: SignalType;
};

/**
 * The management modal's locked state. Shaped like the intent banner it sits beside -- request, then an
 * inline success -- rather than the filter panel's gate, because the modal has no upgrade icon to fall
 * back on and the user is already here to configure something.
 */
export const SignalTypeLockedBanner = ({ signalType }: SignalTypeLockedBannerProps) => {
    const { translate } = useTranslate();
    const [isRequestSent, setIsRequestSent] = useState(false);

    if (isRequestSent) {
        return (
            <SuccessBannerContent
                title={translate(featureHookKeys.modalSuccessTitle)}
                subTitle={translate(featureHookKeys.modalSuccessSubtitle)}
            />
        );
    }

    const handleClick = (): void => {
        requestSignalTypeAccessBySignalType(signalType);
        setIsRequestSent(true);
    };

    return (
        <GrandAccessBanner
            title={translate(signalsI18nKeys.lockedState.title(signalType))}
            description={translate(signalsI18nKeys.lockedState.subtitle(signalType))}
            iconName={getIconForSignalType(signalType)}
            isRounded
            button={
                <IconButton
                    type="trial"
                    onClick={handleClick}
                    iconName="sparkles"
                    dataAutomation={`signals-${signalType}-locked-banner-cta`}
                >
                    {translate(featureHookKeys.speakWithUsCta)}
                </IconButton>
            }
        />
    );
};
